import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { flows, ussd } from '@repletoryreach/sdk-api';
import { useBots } from '../../../lib/bots';

/**
 * Channels a flow runs on because of its bot rather than its trigger
 * (4 October 2026): a USSD code given to the flow's bot (or, for a code given
 * to nobody, to the default bot) is answered by that bot's flows, so the
 * builder warns about the steps USSD cannot run even when the trigger names
 * no channel.
 *
 * Asked without retries: an account with no USSD, or a USSD service that is
 * not there, lists no codes and the flow is checked by its trigger alone.
 */
export function useBotChannels(botId: string | null | undefined): string[] {
  const { defaultBot } = useBots();
  const codes = useQuery({ queryKey: ['ussd-codes'], queryFn: () => ussd.listCodes(), staleTime: 60_000, retry: false });
  const live = useMemo(() => (codes.data?.data ?? []).filter((c) => c.status !== 'released'), [codes.data]);
  const links = useQuery({
    queryKey: ['bots', 'channel-links'],
    queryFn: () => flows.listBotChannelLinks(),
    staleTime: 60_000,
    retry: false,
    enabled: live.length > 0
  });

  return useMemo(() => {
    if (live.length === 0) return [];
    // A flow that names no bot is the default bot's.
    const bot = botId ?? defaultBot?.id ?? null;
    if (!bot) return [];
    const rows = links.data?.data ?? [];
    const answers = live.some((c) => (rows.find((l) => l.channel === 'ussd' && l.account_id === c.id)?.bot_id ?? defaultBot?.id) === bot);
    return answers ? ['ussd'] : [];
  }, [live, links.data, botId, defaultBot?.id]);
}
